import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Crown, Lock, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTimeUntilReset } from '@/lib/dailyUsage';

interface UsageBadgeProps {
  used: number;
  limit: number;
  label?: string;
  resetType?: 'daily' | 'weekly';
  isPremium?: boolean;
  isLocked?: boolean;
  compact?: boolean;
  className?: string;
  onClick?: () => void;
}

export function UsageBadge({
  used,
  limit,
  label,
  resetType = 'daily',
  isPremium = false,
  isLocked = false,
  compact = false,
  className,
  onClick,
}: UsageBadgeProps) {
  const remaining = Math.max(0, limit - used);
  const isExhausted = remaining <= 0;
  const isUnlimited = limit === Infinity || !isFinite(limit);
  const resetTimer = getTimeUntilReset(resetType);

  if (isLocked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Badge
              variant="outline"
              className={cn(
                "gap-1 cursor-pointer text-muted-foreground border-muted-foreground/30 hover:bg-muted/50",
                className
              )}
              onClick={onClick}
            >
              <Lock className="h-3 w-3" />
              {compact ? 'Locked' : 'Premium Only'}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">Upgrade to Premium to unlock</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (isUnlimited) {
    return (
      <Badge
        variant="outline"
        className={cn(
          "gap-1 text-emerald-600 border-emerald-200 bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800 dark:bg-emerald-950/30",
          className
        )}
      >
        <Crown className="h-3 w-3" />
        {compact ? '∞' : 'Unlimited'}
      </Badge>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge
            variant="outline"
            className={cn(
              "gap-1 tabular-nums transition-colors",
              isExhausted
                ? "text-destructive border-destructive/30 bg-destructive/5 cursor-pointer hover:bg-destructive/10"
                : remaining <= 1
                  ? "text-amber-600 border-amber-200 bg-amber-50 dark:text-amber-400 dark:border-amber-800 dark:bg-amber-950/30"
                  : "text-muted-foreground border-border",
              onClick && "cursor-pointer",
              className
            )}
            onClick={isExhausted ? onClick : undefined}
          >
            {isExhausted && !isPremium && <Crown className="h-3 w-3" />}
            {compact
              ? `${remaining}/${limit}`
              : `${used}/${limit} ${label || 'used'}`
            }
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <p className="font-medium">
            {isExhausted
              ? isPremium
                ? 'Limit reached for today'
                : 'Limit reached'
              : `${remaining} remaining`
            }
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Resets in {resetTimer}
          </p>
          {isExhausted && !isPremium && (
            <p className="text-xs text-primary font-medium">Upgrade for more →</p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
